/**
 * @unscoped — 대화 초대 후보 role 정책 SSOT (2026-08-11).
 *
 * 배경:
 *   - backend `GET /api/v1/users` (users.repository.listByInstitution) 는 role 필터 없이
 *     기관 전체를 반환한다.
 *   - 초대 실행 endpoint `POST /api/v1/chat/rooms/:id/members` (rooms.routes.ts) 도 대상의
 *     role 을 보지 않는다 — 같은 기관 · isActive · 비중복이면 허용. 즉 **서버는 관리자 초대를
 *     이미 허용**하며, 모바일(iOS/AOS RoomDrawerViewModel.searchUsers) 도 role 필터가 없어
 *     시스템관리자를 초대할 수 있었다.
 *   - 막고 있던 것은 웹 InviteMembersModal 의 로컬 상수 하나뿐이었다(커밋 1f0f5c7, 2026-07-01
 *     "대화 초대 후보에서 관리자·AI봇 제외"). 같은 웹 안에서도 NewChatModal(새 채팅)에는 필터가
 *     없어 "새 대화로는 되는데 기존 방 초대는 안 되는" 비대칭이 있었다.
 *
 * 정책(2026-08-11 변경):
 *   - 사람 계정(SYSTEM_ADMIN · ADMIN · MANAGER · STAFF)은 모두 초대 가능.
 *   - AI_BOT 만 제외 — AI 는 방 aiMode / '/ai' 명령이라는 전용 경로로 참여하므로 사람 후보
 *     목록에 섞이면 오초대가 된다.
 *   - role 을 알 수 없으면(undefined/null) 후보로 남긴다 — 최종 판정은 서버(권한·기관·활성).
 *
 * UserRole enum SSOT: prisma/schema.prisma (SYSTEM_ADMIN · ADMIN · MANAGER · STAFF · AI_BOT).
 * 초대 권한(누가 초대할 수 있는가)은 본 모듈의 관심사가 아니다 — 방 role(OWNER/ADMIN) 기준으로
 * 서버가 판정하고 ChatParticipantPanel 이 '+' 노출을 제어한다.
 */

import type { ChatI18nKey } from './chat-i18n';

/** 초대 후보에서 제외할 user role. 사람 계정은 제외하지 않는다. */
export const INVITE_EXCLUDED_ROLES: ReadonlySet<string> = new Set(['AI_BOT']);

/** 해당 role 이 대화 초대 후보로 노출 가능한지. role 미상은 true(서버가 최종 판정). */
export function isInvitableRole(role: string | null | undefined): boolean {
  if (!role) return true;
  return !INVITE_EXCLUDED_ROLES.has(role);
}

/** 사용자 목록에서 초대 불가 role 을 걸러낸다. 원본 순서 유지. */
export function filterInviteCandidates<T extends { role?: string | null }>(users: readonly T[]): T[] {
  return users.filter((u) => isInvitableRole(u.role));
}

/**
 * 후보 행에 노출할 role 배지 i18n key. 일반 직원(MANAGER/STAFF)은 배지 없음(null).
 * 관리자 계정을 사람과 구분해 보여줘 오초대를 막는 용도.
 */
const ROLE_BADGE_KEYS: Readonly<Record<string, ChatI18nKey>> = {
  SYSTEM_ADMIN: 'chat.invite.role_system_admin',
  ADMIN: 'chat.invite.role_admin',
};

export function inviteRoleBadgeKey(role: string | null | undefined): ChatI18nKey | null {
  if (!role) return null;
  return ROLE_BADGE_KEYS[role] ?? null;
}
